/* eslint-disable react/jsx-one-expression-per-line */
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { HiOutlineChevronRight } from 'react-icons/hi';

import TransparentButton from '@/atoms/button-transparent';
import Carosuel from '@/atoms/image-slider';
import NumberFormat from '@/atoms/number-format';
import { OfficeDto } from '@graphql/hooks-and-types';

export const PROPERTY_CARD_WIDTH = 284;
export const PROPERTY_CARD_HEIGHT = 230;

export interface PropertyCardProps {
  property: OfficeDto;
  isRowView: boolean;
  onClick?: () => void;
  isFocused?: boolean;
}

export const Amenity = ({ icon, value }: { icon: string; value: string }) => {
  return (
    <div className="flex mr-3 flex-row items-center justify-start text-base md:text-xl text-left">
      <Image src={icon} width={20} height={20} alt={value} className="mr-2" style={{ objectFit: 'contain' }} />
      <span className="text-xs">{value}</span>
    </div>
  );
};

// eslint-disable-next-line complexity
const PropertyCard = ({ property, isRowView, onClick, isFocused }: PropertyCardProps) => {
  const router = useRouter();
  const images = useMemo(() => {
    return (
      property?.listingMedia?.map((media) => ({
        url: media.url,
      })) || [{ url: '/placeholder.webp' }]
    );
  }, [property?.listingMedia]);

  return (
    <TransparentButton
      className="h-auto !cursor-pointer w-auto !m-0 !p-0"
      style={{ textAlign: 'start' }}
      onClick={onClick}
    >
      <div
        className={`${isRowView ? 'mr-0 w-[270px] !mb-0' : 'grid grid-cols-1'} ${
          isFocused && 'border-[1px] border-solid border-light-error rounded-xl'
        } mb-6 md:mb-0 py-2 md:mr-0 mr-3 px-1 justify-items-center shadow-sm md:shadow-xl bg-white rounded-xl items-end`}
      >
        {/*  Image Slider */}
        <div className={`${isRowView ? 'w-[262px]' : 'w-full'} px-1`}>
          <Carosuel showThumbs={false} height={isRowView ? 100 : 172} images={images} />
        </div>
        <div className="px-2 w-full mt-1 md:mt-2">
          {!isRowView && (
            <div className="w-full pb-1 md:pb-2 mb-2 text-[10px] font-medium border-0 border-b-[1px] uppercase border-gray-200 text-gray-500 border-solid">
              {property.propertyType || '-'}
            </div>
          )}
          <div className="w-full justify-items-left text-black text-xl md:text-base mb-2 md:mb-2">
            <span className="font-bold mr-2">
              <NumberFormat accuracy={10} prefix={property?.location?.currency?.symbol || '$'}>
                {Number(property.baseRate) || 0}
              </NumberFormat>
            </span>
          </div>

          <div className="items-start h-6" title={property.name}>
            <span
              style={{ color: '#707070' }}
              className={`${isRowView ? 'h-6 !line-clamp-1' : 'h-5'} text-sm md:text-sm text-start line-clamp-2`}
            >
              {property.name || '-'}
            </span>
          </div>
          <div className={`items-start ${isRowView ? 'h-6' : 'h-12'}`} title={property.address}>
            <span
              style={{ color: '#707070' }}
              className={`${isRowView ? 'h-6 !line-clamp-1' : 'h-10 mb-3'} text-sm md:text-sm text-start line-clamp-2`}
            >
              {property.address || '-'}
            </span>
          </div>

          <div className="flex flex-row mt-0 justify-between justify-items-center">
            <div
              style={{ color: '#707070' }}
              className={`${
                isRowView ? 'mb-2 mt-2' : 'mb-4'
              } px-0 flex flex-row h-5 w-fit md:text-lg justify-start items-center overflow-hidden flex-wrap`}
            >
              {property.baseAttributes.map((attribute) => (
                <Amenity key={attribute.key} value={attribute.value} icon={attribute.icon} />
              ))}
            </div>
            {!isRowView && (
              <Link
                prefetch={false}
                target="_blank"
                href={`/rentals/${property.id}?clientName=${router.query.clientName}`}
                className="h-6 pl-3 py-0 m-0 rounded-full no-underline hover:!text-white text-white flex flex-row items-center justify-center"
                style={{ background: 'var(--error)', textDecoration: 'none' }}
                color="var(--error)"
              >
                <span className="text-xs m-0">More</span>
                <HiOutlineChevronRight className="mr-1 ml-1" size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </TransparentButton>
  );
};

export default PropertyCard;
